import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import './SaveAccountModal.css';

interface SaveAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerCoinName?: string;
  onNavigate?: (view: 'privacy' | 'terms') => void;
}

export const SaveAccountModal: React.FC<SaveAccountModalProps> = ({
  isOpen,
  onClose,
  triggerCoinName,
  onNavigate
}) => {
  const [loadingProvider, setLoadingProvider] = useState<'discord' | null>(null);
  const [msg, setMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  if (!isOpen) return null;

  const handleOAuthSignIn = async (provider: 'discord') => {
    try {
      setLoadingProvider(provider);
      setMsg(null);

      const { data: linkData, error: linkErr } = await supabase.auth.linkIdentity({
        provider,
        options: {
          redirectTo: window.location.origin
        }
      });

      if (linkData?.url) {
        window.location.href = linkData.url;
        return;
      }

      if (linkErr) {
        const { data: oauthData, error: oauthErr } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: window.location.origin
          }
        });
        if (oauthErr) throw oauthErr;
        if (oauthData?.url) {
          window.location.href = oauthData.url;
          return;
        }
      }
    } catch (err: any) {
      console.error('OAuth sign-in failed:', err);
      setMsg({
        text: err.message || 'Failed to sign in with Discord.',
        type: 'error'
      });
      setLoadingProvider(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          &times;
        </button>

        <h3 className="modal-title">Secure Your Finds</h3>
        <p className="modal-subtitle">
          {triggerCoinName
            ? `You just pulled ${triggerCoinName}! Sign in with Discord to save your collection permanently.`
            : 'Save your wallet balance and collection album permanently across all your devices.'}
        </p>

        <div className="modal-highlight-box">
          <strong>Free Account Benefits:</strong>
          <br />
          • Instant cloud backup of all your found coins
          <br />
          • Seamless sync between Desktop & Mobile
        </div>

        <div className="oauth-buttons-container">
          {/* Discord Sign In */}
          <button
            className="oauth-btn discord"
            onClick={() => handleOAuthSignIn('discord')}
            disabled={loadingProvider !== null}
          >
            <span className="oauth-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </span>
            {loadingProvider === 'discord' ? 'Connecting to Discord...' : 'Continue with Discord'}
          </button>
        </div>

        <div className="modal-legal-notice">
          By signing in, you agree to our{' '}
          <a href="/terms" onClick={(e) => { e.preventDefault(); onClose(); if (onNavigate) onNavigate('terms'); }}>
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" onClick={(e) => { e.preventDefault(); onClose(); if (onNavigate) onNavigate('privacy'); }}>
            Privacy Policy
          </a>
          .
        </div>

        {msg && <div className={`modal-msg ${msg.type}`}>{msg.text}</div>}
      </div>
    </div>
  );
};
